import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileScan, Loader2, UploadCloud, FileText } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const FIELD_LABELS = [
  ["document_type", "Document Type"],
  ["holder_name_masked", "Holder Name (masked)"],
  ["document_number_masked", "Document No. (masked)"],
  ["issue_date", "Issue Date"],
  ["expiry_date", "Expiry Date"],
];

export default function OcrTester({ onActivity }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const pick = (f) => {
    if (!f) return;
    const ok = ["image/png", "image/jpeg", "image/webp", "application/pdf"].includes(f.type);
    if (!ok) {
      toast.error("Use a PNG, JPEG, WEBP image or PDF");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const run = async () => {
    if (!file) {
      toast.error("Select a document first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/ocr-test", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      toast.success("Document fields extracted and masked");
      onActivity?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR extraction failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-none border-border shadow-sm" data-testid="ocr-tester-card">
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-center gap-2">
          <FileScan className="h-4 w-4 text-teal-600" strokeWidth={2.2} />
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.12em] text-primary">
            Vision OCR — Document Expiry Extraction
          </CardTitle>
        </div>
        <CardDescription className="text-[13px]">
          Upload a passport, visa or ID. Holder identity is tokenized before storage.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-5 space-y-4">
        <div
          data-testid="ocr-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pick(e.dataTransfer.files?.[0]);
          }}
          className={`border-2 border-dashed cursor-pointer px-6 py-9 text-center transition-colors ${
            dragOver ? "border-teal-600 bg-teal-50/50" : "border-border bg-slate-50/60 hover:border-slate-400"
          }`}
        >
          <input
            ref={inputRef}
            data-testid="ocr-file-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => pick(e.target.files?.[0])}
          />
          <UploadCloud className="h-6 w-6 mx-auto text-slate-400 mb-2" strokeWidth={1.8} />
          {file ? (
            <p className="text-[13px] font-medium text-slate-700 flex items-center justify-center gap-2" data-testid="ocr-file-name">
              <FileText className="h-4 w-4 text-teal-600" />
              {file.name} <span className="text-muted-foreground font-normal">({Math.round(file.size / 1024)} KB)</span>
            </p>
          ) : (
            <p className="text-[13px] text-muted-foreground">
              Drop a document here or <span className="text-teal-700 font-medium">browse</span>
              <span className="block text-[11px] mt-1 font-mono">PNG · JPEG · WEBP · PDF, max 10MB</span>
            </p>
          )}
        </div>

        <Button
          data-testid="ocr-run-btn"
          onClick={run}
          disabled={loading || !file}
          className="rounded-none bg-primary hover:bg-slate-700 text-white h-9 px-5 text-[13px] font-medium"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileScan className="h-4 w-4" />}
          {loading ? "Extracting via vision model..." : "Run OCR Extraction"}
        </Button>

        {result && (
          <div className="fade-in space-y-3" data-testid="ocr-results">
            <div className="border border-border divide-y divide-border">
              {FIELD_LABELS.map(([key, label]) => (
                <div key={key} className="flex items-center px-4 py-2.5 bg-white">
                  <span className="w-52 shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    {label}
                  </span>
                  <span
                    data-testid={`ocr-field-${key}`}
                    className={`text-[13px] ${
                      key.includes("masked")
                        ? "font-mono text-teal-800 bg-teal-50 px-1.5 py-0.5"
                        : key === "expiry_date"
                        ? "font-semibold text-primary"
                        : "text-slate-700"
                    }`}
                  >
                    {result[key] ?? "—"}
                  </span>
                  {key === "document_type" && (
                    <Badge variant="outline" className="rounded-none ml-3 text-[10px] uppercase font-mono border-slate-300 text-slate-500">
                      {result.model}
                    </Badge>
                  )}
                </div>
              ))}
              <div className="flex items-center px-4 py-2.5 bg-white">
                <span className="w-52 shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Confidence
                </span>
                <div className="flex items-center gap-3 flex-1">
                  <div className="h-1.5 bg-slate-100 w-40">
                    <div
                      className="h-1.5 bg-teal-600"
                      style={{ width: `${Math.round((result.confidence ?? 0) * 100)}%` }}
                    />
                  </div>
                  <span className="text-[13px] font-mono text-slate-700" data-testid="ocr-field-confidence">
                    {Math.round((result.confidence ?? 0) * 100)}%
                  </span>
                </div>
              </div>
            </div>
            <p className="text-[11px] font-mono text-muted-foreground" data-testid="ocr-meta">
              {result.latency_ms}ms · audit {result.audit_id.slice(0, 8)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
